import { CONSTANTS } from "@/util/constants";
import { fixSafariCursor, getCaretOffsetWithin, setCaretOffsetWithin } from "@/util/methods";
import { cloneDeep, isEqual } from "lodash";
import { type CSSProperties, computed, nextTick, ref, watch } from "vue";
import type { Ref } from "vue";

export interface PickerOption {
  src?: string;
  label?: string;
  id?: string;
  [key: string]: any;
}

export type PicklistOption = string | PickerOption;

interface UsePicklistOptionEditorParams {
  /** The parent-bound picklist options (from props). */
  picklistOptions: Ref<PicklistOption[]>;
  /** The parent field object (used for id, columnsNumber, spreadColumns). */
  field: Ref<Record<string, any>>;
  /** Current layout constant (1 or TWO_COLUMN). */
  layout: Ref<number>;
  /** Whether this is a saved custom field (triggers delete confirmation modal). */
  customFieldCreated: Ref<boolean>;
  /** i18n translate function. */
  t: (key: string) => string;
  /** Called whenever the local option list changes and should propagate to the parent. */
  onOptionsChange: (options: PicklistOption[]) => void;
}

/**
 * @description Composable that manages the full option-list editing lifecycle for
 * CheckboxField and RadioField. Provides:
 * - Bidirectional sync between the parent prop and a local draggable-safe ref.
 * - Add / remove / reorder operations with modal confirmation for custom fields.
 * - Inline label editing with caret preservation.
 * - Shared UX helpers (focus, editing, safari cursor fix).
 * - Responsive column-width calculation.
 *
 * @param params - Configuration.
 * @returns Reactive state and event handlers.
 */
export const usePicklistOptionEditor = ({
  picklistOptions,
  field,
  layout,
  customFieldCreated,
  t,
  onOptionsChange,
}: UsePicklistOptionEditorParams) => {
  const localPicklistOptions = ref<PicklistOption[]>(cloneDeep(picklistOptions.value));

  /** Guards against recursive updates between the two-way sync watchers. */
  const isInternalUpdate = ref(false);

  /** Syncs parent → local when the prop changes externally. */
  watch(
    () => picklistOptions.value,
    (newVal) => {
      if (!isInternalUpdate.value && !isEqual(newVal, localPicklistOptions.value)) {
        localPicklistOptions.value = cloneDeep(newVal);
      }
      isInternalUpdate.value = false;
    },
    { deep: true, immediate: true }
  );

  /** Syncs local → parent when the user edits options. */
  watch(
    () => localPicklistOptions.value,
    (newVal) => {
      if (!isEqual(newVal, picklistOptions.value)) {
        isInternalUpdate.value = true;
        onOptionsChange(cloneDeep(newVal));
      }
    },
    { deep: true }
  );

  /**
   * @description Calculates the percentage or fixed width for each option row based
   * on the current layout and `columnsNumber`/`spreadColumns` field settings.
   *
   * @returns {string} CSS width value (e.g. "50%" or "100%").
   */
  const getColumnWidth = (): string => {
    let widthPercent = 100;
    if (layout.value === CONSTANTS.TWO_COLUMN) {
      widthPercent = Math.max(widthPercent / field.value.columnsNumber, 50);
    } else {
      widthPercent = Math.max(widthPercent / field.value.columnsNumber, 20);
    }
    return field.value.spreadColumns ? `${widthPercent}%` : "100%";
  };

  /** Inline style that constrains each option row to the computed column width. */
  const minimumWidth = computed((): CSSProperties => ({ width: getColumnWidth() }));

  /** True when the last option's label is empty – used to disable the "Add option" button. */
  const isLastOptionEmpty = computed(() => {
    const lastOption = picklistOptions.value[picklistOptions.value.length - 1];
    if (typeof lastOption === "string") return lastOption.trim() === "";
    return lastOption?.label?.trim() === "";
  });

  /** Index of the option pending deletion; null when no deletion is in progress. */
  const indexToRemove = ref<number | null>(null);
  /** Controls visibility of the delete-confirmation modal. */
  const showDeleteModal = ref(false);

  /**
   * @description Focuses the label element at the given index and selects all its
   * content for immediate typing. Falls back to querying by element ID when the
   * ref array doesn't have the element yet.
   *
   * @param insertAt - Index of the newly inserted option.
   * @param getLabelRef - Optional ref getter from `useBubbleLabelSync`.
   * @returns {void}
   */
  const focusOptionLabel = (
    insertAt: number,
    getLabelRef?: (i: number) => HTMLElement | null
  ): void => {
    nextTick(() => {
      const el =
        getLabelRef?.(insertAt) ??
        (document.getElementById(`option-label-${field.value.id}-${insertAt}`) as HTMLElement | null);
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    });
  };

  /**
   * @description Inserts a new option after the given index and focuses its label.
   *
   * @param index - The option index after which to insert.
   * @param getLabelRef - Optional label ref getter for post-insert focus.
   * @returns {void}
   */
  const addOptionAt = (index: number, getLabelRef?: (i: number) => HTMLElement | null): void => {
    const newOptions = [...localPicklistOptions.value];
    const insertAt = Math.min(Math.max(index + 1, 0), newOptions.length);

    if (typeof newOptions[0] === "string") {
      newOptions.splice(insertAt, 0, t("common.typeAnOption"));
    } else {
      newOptions.splice(insertAt, 0, {
        id: `option-${Date.now()}`,
        label: t("common.typeAnOption"),
      });
    }

    localPicklistOptions.value = newOptions;
    focusOptionLabel(insertAt, getLabelRef);
  };

  /**
   * @description Appends a new option at the end of the list.
   *
   * @param getLabelRef - Optional label ref getter for post-insert focus.
   * @returns {void}
   */
  const addOption = (getLabelRef?: (i: number) => HTMLElement | null): void => {
    addOptionAt((localPicklistOptions.value?.length ?? 1) - 1, getLabelRef);
  };

  /**
   * @description Removes an option, showing a confirmation modal for custom fields.
   * No-ops when only one option remains.
   *
   * @param index - Index of the option to remove.
   * @returns {void}
   */
  const prepareToRemoveOption = (index: number): void => {
    if (picklistOptions.value.length <= 1) return;
    if (customFieldCreated.value) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      const newOptions = [...localPicklistOptions.value];
      newOptions.splice(index, 1);
      localPicklistOptions.value = newOptions;
    }
  };

  /**
   * @description Confirms the pending deletion and removes the option from the list.
   *
   * @returns {void}
   */
  const removeOption = (): void => {
    if (indexToRemove.value !== null) {
      const newOptions = [...localPicklistOptions.value];
      newOptions.splice(indexToRemove.value, 1);
      localPicklistOptions.value = newOptions;
    }
    showDeleteModal.value = false;
  };

  /**
   * @description Cancels a pending deletion without removing any option.
   *
   * @returns {void}
   */
  const cancelRemoveOption = (): void => {
    indexToRemove.value = null;
    showDeleteModal.value = false;
  };

  /**
   * @description Updates the label text of an option at the given index while
   * preserving the user's caret position after Vue re-renders.
   *
   * @param event - The native input event from the contenteditable label.
   * @param index - The option index whose label is being edited.
   * @param getLabelRef - Optional label ref getter for accurate caret restoration.
   * @returns {void}
   */
  const updateOptionLabel = (
    event: Event,
    index: number,
    getLabelRef?: (i: number) => HTMLElement | null
  ): void => {
    const target = event.target as HTMLElement;
    const text = target.innerText.replace(/\u200B/g, "");
    const caret = getCaretOffsetWithin(target);

    if (typeof localPicklistOptions.value[index] === "string") {
      (localPicklistOptions.value as PicklistOption[])[index] = text;
    } else {
      (localPicklistOptions.value[index] as PickerOption).label = text;
    }

    nextTick(() => {
      const el = getLabelRef?.(index) ?? target;
      setCaretOffsetWithin(el, caret);
    });
  };

  /**
   * @description Handles focus on a contenteditable option label.
   * Selects all text when the label has content, or places the caret at the start
   * with a zero-width-space placeholder when it is empty.
   *
   * @param event - The native focus event.
   * @returns {void}
   */
  const handleFocus = (event: FocusEvent): void => {
    const target = event.target as HTMLElement;
    const selection = window.getSelection();
    if (selection) selection.removeAllRanges();

    const sanitized = (target.textContent ?? "").replace(/\u200B/g, "").trim();
    if (!sanitized) {
      let textNode = target.firstChild as Text | null;
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
        target.textContent = "";
        textNode = document.createTextNode("\u200B");
        target.appendChild(textNode);
      }
      if (!textNode.textContent) textNode.textContent = "\u200B";
      const range = document.createRange();
      range.setStart(textNode, 0);
      range.collapse(true);
      selection?.addRange(range);
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(target);
    selection?.addRange(range);
  };

  /**
   * @description Cleans up zero-width space artifacts when the user empties a label
   * and resets the caret to the start.
   *
   * @param event - The native keyup event.
   * @returns {void}
   */
  const handleEditing = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement;
    if (target.textContent?.trim() === "" && target.textContent) {
      target.textContent = "";
      const range = document.createRange();
      range.setStart(target, 0);
      range.collapse(true);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  /**
   * @description Fixes the Safari cursor position on keydown/keyup events inside
   * contenteditable labels.
   *
   * @param event - The native keyboard or input event.
   * @returns {void}
   */
  const handleSafariCursor = (event: Event): void => {
    fixSafariCursor(event, event.type === "keydown");
  };

  return {
    localPicklistOptions,
    minimumWidth,
    isLastOptionEmpty,
    indexToRemove,
    showDeleteModal,
    getColumnWidth,
    addOption,
    addOptionAt,
    prepareToRemoveOption,
    removeOption,
    cancelRemoveOption,
    updateOptionLabel,
    handleFocus,
    handleEditing,
    handleSafariCursor,
  };
};
